import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { IZipCode, ZipCode } from '../models/zip-code.model';

/**
 * SERVICE CopomexService
 *
 * Consulta la API pública de códigos postales (Copomex, catálogo de SEPOMEX)
 * para autorrellenar Ciudad y Estado a partir de un código postal.
 *
 * Este servicio se aparta A PROPÓSITO de cuatro costumbres del resto de los
 * servicios del proyecto. No es descuido:
 *
 * 1. NO ENVÍA TOKEN.
 *    La API es pública y no lleva header `Authorization`; mandarlo no aporta
 *    nada. Ya hay precedente de llamar a otro dominio sin token en
 *    `camera.service.ts` (método screenshot). Además el proyecto NO tiene
 *    interceptor HTTP global, así que ningún header se cuela solo: lo que no
 *    se escribe aquí, no se manda.
 *
 * 2. NO MUESTRA TOASTS.
 *    Los demás servicios disparan `toastService` ante un error. Aquí el error
 *    pertenece a un campo del formulario (el código postal) y debe aparecer
 *    junto a ese campo, no flotando en una esquina de la pantalla. El servicio
 *    devuelve el resultado y el componente decide qué pintar.
 *
 * 3. NO USA spinnerService.
 *    `spinnerService` es un overlay global que bloquea toda la pantalla.
 *    Bloquear la aplicación entera porque alguien tecleó un código postal
 *    sería desproporcionado. El indicador de carga vive junto al campo y lo
 *    maneja el componente.
 *
 * 4. NO LLAMA NADA DESDE EL CONSTRUCTOR.
 *    Otros servicios hacen `this.getAll()` al construirse. Este solo actúa
 *    bajo demanda, cuando el usuario escribe un código postal.
 */
@Injectable({
  providedIn: 'root'
})
export class CopomexService {

  constructor(
    private http: HttpClient
  ) { }

  /*
  ███████╗███████╗ █████╗ ██████╗  ██████╗██╗  ██╗
  ██╔════╝██╔════╝██╔══██╗██╔══██╗██╔════╝██║  ██║
  ███████╗█████╗  ███████║██████╔╝██║     ███████║
  ╚════██║██╔══╝  ██╔══██║██╔══██╗██║     ██╔══██║
  ███████║███████╗██║  ██║██║  ██║╚██████╗██║  ██║
  ╚══════╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝

  */
  /**
   * FUNCTION searchByZipCode
   *
   * Consulta las colonias de un código postal.
   *
   * El código postal viaja como PATH PARAMETER y debe medir exactamente 5
   * caracteres; con menos, la API responde 422. El filtrado a 5 dígitos se
   * hace en el componente antes de llamar aquí.
   *
   * ⚠️ Un código postal inexistente NO devuelve 404: devuelve 200 con
   * `data.neighborhoods` vacío. La detección de "no encontrado" es, por tanto,
   * `resultado.length === 0` del lado del componente, no un catch.
   *
   * El error de red o de servidor sí se propaga (throw) para que el componente
   * pueda distinguirlo de "no encontrado" y pintar un mensaje distinto.
   *
   * Ojo: corre en Lambda sin concurrencia provisionada, así que el primer
   * llamado puede tardar entre 1 y 3 segundos.
   *
   * @param zipCode código postal de 5 dígitos
   * @returns lista de colonias del código postal; vacía si no existe
   */
  async searchByZipCode(zipCode: string): Promise<ZipCode[]> {

    const urlApi = `${environment.urlCopomexApi}/zipcodes/${zipCode}`;

    const response = await firstValueFrom(
      this.http.get<IServerResponse<{ neighborhoods: IZipCode[] }>>(
        urlApi
      ).pipe(
        map(res => new ServerResponse<{ neighborhoods: IZipCode[] }>(res))
      )
    );

    if (response.statusCode !== 200) {
      throw new Error(response.message);
    }

    return (response.data?.neighborhoods ?? []).map(item => new ZipCode(item));
  }

}
